// src/lib/constants/stream.ts

import type { SortDirection } from '@/lib/types/common';
import type { StreamSortKey } from '@/lib/types/Stream';

export const STREAM_TYPES = ['push', 'pull', 'simulatedlive'] as const;
export type StreamType = typeof STREAM_TYPES[number];

export const STREAM_TYPE_LABELS: Record<StreamType, string> = {
  push: 'Live Push',
  pull: 'Live Pull',
  simulatedlive: 'Simulated Live',
} as const;

export const STREAM_RECORD_MODES = ['manual', 'automatic', 'schedule'] as const;
export type StreamRecordMode = typeof STREAM_RECORD_MODES[number];

/** Only manual and automatic shown in create UI; schedule is edit-only */
export const STREAM_CREATE_RECORD_MODES = ['manual', 'automatic'] as const;

export const STREAM_ADAPTIVE_MODES = ['none', 'publisher', 'transcode_event', 'transcode_continuous'] as const;
export type StreamAdaptiveMode = typeof STREAM_ADAPTIVE_MODES[number];

export const STREAM_OOS_MODES = ['offline', 'online'] as const;
export type StreamOosMode = typeof STREAM_OOS_MODES[number];

export const STREAM_OFFLINE_DISPLAY_MODES = ['default_message', 'custom_message', 'offline_poster', 'media', 'list'] as const;
export type StreamOfflineDisplayMode = typeof STREAM_OFFLINE_DISPLAY_MODES[number];

export const DVR_ARCHIVE_STRATEGY_TYPES = ['append', 'delete'] as const;
export type DvrArchiveStrategyType = typeof DVR_ARCHIVE_STRATEGY_TYPES[number];

export const STREAM_OUTPUT_SOURCE_TYPES = ['publisher', 'transcoder'] as const;
export type StreamOutputSourceType = typeof STREAM_OUTPUT_SOURCE_TYPES[number];

/** Stream grid column definitions */
export const STREAM_GRID_COLUMNS = [
  { key: 'checkbox', label: '', width: 48, sortable: false, align: 'center' as const },
  { key: 'title', label: 'Title', width: 'auto', sortable: true, align: 'left' as const },
  { key: 'liveStatus', label: 'Live Status', width: 120, sortable: true, align: 'center' as const },
  { key: 'location', label: 'Location', width: 140, sortable: true, align: 'left' as const },
  { key: 'status', label: 'Status', width: 100, sortable: true, align: 'center' as const },
  { key: 'published', label: 'Published', width: 150, sortable: true, align: 'right' as const },
  { key: 'updated', label: 'Updated', width: 150, sortable: true, align: 'right' as const },
  { key: 'actions', label: '', width: 60, sortable: false, align: 'center' as const },
] as const;

/** Default sort when no persisted preference exists: published desc */
export const DEFAULT_STREAM_SORT_KEY: StreamSortKey = 'published';
export const DEFAULT_STREAM_SORT_DIRECTION: SortDirection = 'desc';

export const STREAM_SORT_KEYS = ['title', 'liveStatus', 'location', 'status', 'published', 'updated'] as const;

/** Live status polling interval (ms) -- 15 seconds matches legacy */
export const LIVE_STATUS_POLL_INTERVAL_MS = 15_000;

/**
 * HTTP-proxy live backends (customer-provided HLS relayed through nginx, no
 * Wowza) are identified by locationLabel prefix — a data convention with the
 * hand-provisioned backend rows ("HTTP Proxy 1", ...). No structural field can
 * distinguish them: every liveBackends row has backendTypeId 2 because the
 * customerinfo query filters on it. Compare lowercased.
 */
export const HTTP_PROXY_LOCATION_PREFIX = 'http proxy';

/** Stream grid row height — fits the 68px poster thumb + padding, matching the video grid */
export const STREAM_ROW_HEIGHT = 84;

/** Virtualizer overscan */
export const STREAM_VIRTUALIZER_OVERSCAN = 10;

/** Containment guards (same approach as video grid) */
export const STREAM_GRID_MAX_VIRTUAL_ROWS = 72;
export const STREAM_GRID_MAX_VIEWPORT_HEIGHT_PX = 2200;

/** Stream field constraints (shared with future form validation) */
export const STREAM_CONSTRAINTS = {
  TITLE_MIN: 1,
  TITLE_MAX: 150,
  DESC_SHORT_MAX: 250,
  OFFLINE_MSG_MAX: 500,
  RECORD_APPEND_TIMEOUT_MIN: 5,
  RECORD_APPEND_TIMEOUT_MAX: 1800,
} as const;

/**
 * Default stream output configuration.
 * Resolution: 540P, Audio: 128 kbps @ 48 kHz.
 */
export const STREAM_DEFAULT_OUTPUT = {
  streamOutputSourceType: 'publisher',
  audioKbps: 128,
  audioSamplerateHz: 48000,
  codec: 'h264_aac',
  videoFps: 30,
  videoKbps: 872,   // 1000 (540P recommended) - 128 audio
  videoWidth: 960,
  videoHeight: 540,
} as const;

/**
 * Default stream create values -- matches legacy VideoStreamView.create().
 */
export const STREAM_CREATE_DEFAULTS = {
  mediaType: 'VideoStream',
  streamType: 'push',
  recordMode: 'manual',
  adaptiveMode: 'none',
  dvr: false,
  dvrArchiveStrategyType: 'append',
  encryptHlsAes: false,
  oosMode: 'offline',
  oosEndMins: null,
  offlineDisplayMode: 'custom_message',
  recordAppendTimeout: 40,
} as const;

/** Live status sort ranking (higher = sorts first in desc) */
export const LIVE_STATUS_RANK = {
  recording: 3,
  live: 2,
  offline: 1,
  unknown: 0,
} as const;

// --- Stream Detail Constants ---

/** Ad injection presets (seconds) */
export const AD_BREAK_DURATIONS = [
  { value: '15', label: '15 seconds' },
  { value: '30', label: '30 seconds' },
  { value: '45', label: '45 seconds' },
  { value: '60', label: '1 minute' },
  { value: '90', label: '1 min 30 sec' },
  { value: '120', label: '2 minutes' },
  { value: '150', label: '2 min 30 sec' },
  { value: '180', label: '3 minutes' },
  { value: '240', label: '4 minutes' },
  { value: '300', label: '5 minutes' },
  { value: 'custom', label: 'Custom' },
] as const;

export const AD_BREAK_MIN_SECONDS = 5;
export const AD_BREAK_MAX_SECONDS = 360;

/** Offline display mode labels (all backend values) */
export const OFFLINE_DISPLAY_MODE_LABELS: Record<StreamOfflineDisplayMode, string> = {
  default_message: 'Default Message',
  custom_message: 'Custom Message',
  offline_poster: 'Offline Poster',
  media: 'Play Video',
  list: 'Play Playlist',
} as const;

/** Editable offline modes in v3 (others shown read-only) */
export const EDITABLE_OFFLINE_MODES: StreamOfflineDisplayMode[] = ['default_message', 'custom_message'];

/** Editable record modes in v3 */
export const EDITABLE_RECORD_MODES: StreamRecordMode[] = ['manual', 'automatic', 'schedule'];

/** Record mode labels for display */
export const RECORD_MODE_LABELS: Record<StreamRecordMode, string> = {
  manual: 'Manually Record',
  automatic: 'Always Record',
  schedule: 'Scheduled Record',
} as const;

// --- Recording Schedule (Scheduled Record mode) ---

/** Recurrence interval wire values ('d'|'w'|'m'|'y') */
export const SCHEDULE_INTERVAL_TYPES = ['d', 'w', 'm', 'y'] as const;
export type ScheduleIntervalType = typeof SCHEDULE_INTERVAL_TYPES[number];

/** Repeats select values: 'never' (one-time) + interval types */
export const SCHEDULE_REPEAT_OPTIONS = ['never', ...SCHEDULE_INTERVAL_TYPES] as const;
export type ScheduleRepeatOption = typeof SCHEDULE_REPEAT_OPTIONS[number];

export const SCHEDULE_INTERVAL_TYPE_LABELS: Record<ScheduleIntervalType, string> = {
  d: 'Daily',
  w: 'Weekly',
  m: 'Monthly',
  y: 'Yearly',
} as const;

export const SCHEDULE_INTERVAL_UNIT_LABELS: Record<ScheduleIntervalType, string> = {
  d: 'day',
  w: 'week',
  m: 'month',
  y: 'year',
} as const;

/** Max "repeat every N" per interval type (legacy ranges) */
export const SCHEDULE_INTERVAL_MAX: Record<ScheduleIntervalType, number> = {
  d: 30,
  w: 20,
  m: 12,
  y: 2,
} as const;

export const STREAM_SCHEDULE_CONSTRAINTS = {
  MAX_DURATION_HOURS: 6,
  MAX_FUTURE_DAYS: 390,          // legacy MaxDate ≈ 13 months
  TITLE_MAX: 150,                // = VIDEO_CONSTRAINTS.TITLE_MAX (recorded VOD title)
  DESC_MAX: 250,                 // = VIDEO_CONSTRAINTS.DESC_SHORT_MAX
} as const;

/** Prefetch threshold for stream detail */
export const STREAM_DETAIL_PREFETCH_MAX_ROWS = 2000;

// --- Stream Output Presets ---

/**
 * Resolution presets for stream outputs.
 * recommendedKbps is the total bitrate (video + audio).
 * Matches legacy StreamOutputUtil.resolutions.
 */
export const STREAM_RESOLUTION_PRESETS = [
  { width: 1920, height: 1080, label: '1920x1080 (1080P)', recommendedKbps: 3500 },
  { width: 1280, height: 720, label: '1280x720 (720P)', recommendedKbps: 2000 },
  { width: 960, height: 540, label: '960x540 (540P)', recommendedKbps: 1000 },
  { width: 768, height: 432, label: '768x432 (432P)', recommendedKbps: 800 },
  { width: 640, height: 360, label: '640x360 (360P)', recommendedKbps: 600 },
  { width: 480, height: 270, label: '480x270 (270P)', recommendedKbps: 400 },
  { width: 640, height: 480, label: '640x480 (4:3)', recommendedKbps: 800 },
  { width: 480, height: 360, label: '480x360 (4:3)', recommendedKbps: 500 },
  { width: 320, height: 240, label: '320x240 (4:3)', recommendedKbps: 300 },
] as const;

export type StreamResolutionPreset = typeof STREAM_RESOLUTION_PRESETS[number];

/** Default resolution index: 1080P */
export const STREAM_DEFAULT_RESOLUTION_INDEX = 0;

/**
 * Default resolution preset index for each output row by position (0-based):
 * 1st output 1080P, 2nd 720P, 3rd 432P. Rows beyond the ladder default to
 * the row above them.
 */
export const STREAM_OUTPUT_DEFAULT_RESOLUTION_INDEXES: readonly number[] = [0, 1, 3];

/** Stream output constraints */
export const STREAM_OUTPUT_CONSTRAINTS = {
  MIN_OUTPUTS: 1,
  MAX_OUTPUTS: 6,
  MIN_BITRATE_KBPS: 150,
  MAX_BITRATE_KBPS: 20000,
} as const;
